import { useState, useEffect, useMemo } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import {
  useClientAuth,
  selectLocationsForDisplay,
  AGENT_PRICE,
  detectAndPersistDemoMode,
  applyDemoNoIndex,
  DEMO_CLIENT,
} from '@bas/shared'
import { useLocalStorage } from '@bas/shared'
import { Sidebar } from '@bas/shared'
import { Header } from '@bas/shared'
import { AvaChatWidget, identifyClarityClient } from '@bas/shared'
import { CLIENT_NAV_ITEMS, CLIENT_SETUP_NAV_ITEM } from '@bas/shared'
import { LocationSwitcher, type Location } from './components/LocationSwitcher'
import { ImpersonationBanner } from './components/ImpersonationBanner'
import { ClientLogin } from './components/ClientLogin'
import { ccPath } from './basePath'
import { warmEndpoints } from './lib/warmEndpoints'
import { useRoadmap, countNeedsClient } from './lib/useRoadmap'
import { auth, LoadingState } from '@bas/shared'
import { signOut } from 'firebase/auth'

/**
 * Root app shell for the Client Control Center.
 *
 * Guards:
 *   - Loading    -> full-page spinner
 *   - No user    -> redirect to marketing site login
 *   - Wrong role -> redirect to marketing site login
 *
 * Layout:
 *   Sidebar (left) + Header (top) + <Outlet /> (main content area)
 *
 * Dark mode is the default per the Stone theme. Clients can toggle to light
 * mode; the preference is persisted in localStorage.
 */

/**
 * The JustTextAva wordmark (2026-09-11).
 *
 * ⚠️ THE BUG THIS REPLACES: `logo.png` / `logo-white.png` are the BEST AI
 * SOLUTIONS wordmark, and they were still here long after this app moved to
 * app.justtextava.com. Jake signed in through a JustTextAva door and landed
 * inside a Best AI Solutions app — half-migrated, since the <title> and the
 * favicons were already JTA.
 *
 * Composed text + the Ava avatar rather than an image, because that is exactly
 * what the login screen renders (`.jta-login-word` in ClientLogin.tsx) — the
 * door and the room now show the same mark rather than two files that have to be
 * kept in agreement by hand.
 */
const AVA_MARK = `${import.meta.env.BASE_URL}Ava2.png`

function JtaWordmark() {
  return (
    <span className="flex items-center gap-2 min-w-0">
      <img
        src={AVA_MARK}
        alt=""
        aria-hidden="true"
        className="w-7 h-7 rounded-full object-cover flex-none border theme-border"
      />
      <span className="font-extrabold text-[17px] tracking-tight theme-text truncate">
        Just<span style={{ color: 'var(--accent)' }}>Text</span>Ava
      </span>
    </span>
  )
}

/** The site's icon via Google's favicon service, or undefined without a usable website. */
function faviconFor(website?: string): string | undefined {
  if (!website) return undefined
  try {
    const host = new URL(/^https?:\/\//i.test(website) ? website : `https://${website}`).hostname
    return host ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=64` : undefined
  } catch {
    return undefined
  }
}

export function App() {
  const { user, loading, unauthorized, client: authClient, clientId: authClientId, isImpersonating, isReviewMode } = useClientAuth()
  // Demo mode: unauthenticated Zoom showcase (/control-center/?demo=<key>). Renders
  // the bundled fixture client (DEMO_CLIENT_DATA via useClientData) — never auths,
  // so Firestore reads stay denied and no real client data is exposed.
  const demoMode = detectAndPersistDemoMode()
  const client = demoMode ? DEMO_CLIENT : authClient
  const clientId = demoMode ? DEMO_CLIENT.id : authClientId
  useEffect(() => { if (demoMode) applyDemoNoIndex() }, [demoMode])
  // Tag the Clarity session with WHO this is. Until 2026-08-29 every recording was
  // anonymous, so a client's session existed and could not be found — see
  // shared/clarity.ts for the case that proved it and the privacy boundary.
  useEffect(() => {
    identifyClarityClient({
      clientId,
      businessName: client?.businessName,
      isDemo: demoMode,
    })
  }, [clientId, client?.businessName, demoMode])
  // Start the read functions behind Setup, Ava's Knowledge and My Tickets as soon as
  // the Control Center opens. MEASURED 2026-09-22: those endpoints answer in 0.13s warm
  // and 6.6s COLD, and the cold cost is container startup, which our own logs cannot see
  // ("execution took N ms" times the handler only). Firing no-argument requests here
  // means the container is already up by the time the client clicks. See
  // lib/warmEndpoints.ts for the probe and for why this runs before minInstances.
  useEffect(() => { if (clientId) warmEndpoints() }, [clientId])
  const navigate = useNavigate()
  // ⚠️ LIGHT IS THE DEFAULT (John, 2026-09-17): *"by default, it opens up in
  // dark mode. And I think it should open in light mode."* This changes only the
  // DEFAULT — `useLocalStorage` means any client who has already chosen a theme
  // keeps their choice; it is the people who have never touched the toggle whose
  // first impression this is.
  const [isDarkMode, setIsDarkMode] = useLocalStorage<boolean>('theme-dark', false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Multi-Location S5: derive LocationSwitcher entries from the live client
  // document instead of the prior `DEMO_LOCATIONS` placeholder. Single-loc
  // clients (no `client.locations` field, or empty array) get ONE synthetic
  // entry built from the top-level `client.address` + gbp* fields — the
  // LocationSwitcher component gates its own visibility (renders nothing
  // when length ≤ 1), so single-loc clients see the standard header layout
  // and multi-loc clients (≥2 active) see the dropdown.
  const switcherLocations = useMemo<Location[]>(
    () => selectLocationsForDisplay(client),
    [client]
  )
  const [activeLocation, setActiveLocation] = useState<string>('')

  // Default active location to the first entry once data loads. Re-runs when
  // the client doc changes (e.g., a location is added/removed via onboarding
  // or Ava intent and the snapshot listener fires).
  useEffect(() => {
    if (switcherLocations.length === 0) return
    const stillExists = switcherLocations.some((l) => l.id === activeLocation)
    if (!activeLocation || !stillExists) {
      setActiveLocation(switcherLocations[0].id)
    }
  }, [switcherLocations, activeLocation])

  // Apply .dark-mode class to <html> to match canonical design system v2
  // architecture (light-default with .dark-mode override) — we add the class to
  // enable dark. ⚠️ The comment here used to say "dark stays the visual
  // default"; John reversed that 2026-09-17, so the stylesheet's own
  // light-default and the app's default now agree.
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark-mode')
    } else {
      document.documentElement.classList.remove('dark-mode')
    }
  }, [isDarkMode])

  // ── Conditional Setup nav (post-2026-05-26 IA) ─────────────────────────────
  // Setup appears as the FIRST sidebar item (with a count badge) only while the
  // client has open setup steps for Ava or any hired agent. These are HOOKS
  // (useClientData + useMemo) and MUST run before the early returns below — the
  // Rules of Hooks require an identical hook sequence on every render. They were
  // previously placed AFTER the `if (loading)` / auth-guard returns, so a loading
  // render ran 2 fewer hooks than a loaded render → React #310, which crashed the
  // Control Center for every authenticated client. Masked until 2026-06-02 by a
  // demo-Firebase-config deploy that blocked all client auth; surfaced + fixed
  // the moment real auth worked. (Found by the authed UI-E2E.)
  // ⚠️ THE BADGE AND THE SETUP PAGE MUST COUNT THE SAME THING. This used to run
  // its OWN filter over the step model — and the comment it replaced recorded
  // that it was the FOURTH hand-kept copy of the in-scope rule, found only after
  // a sweep had already "consolidated the other three", because this one was
  // spelled inverted. Four copies of a rule is the argument for zero: the count
  // now comes from the same served derivation the page renders.
  // [[feedback_two_lists_kept_in_agreement_by_hand]]
  const { view: roadmapView } = useRoadmap(clientId)
  const pendingSetupCount = countNeedsClient(roadmapView)

  // ── Loading spinner ────────────────────────────────────────────────────────
  // Demo mode skips the loading + auth guards and renders with the fixture client.
  if (loading && !demoMode) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: 'var(--bg-body)' }}
      >
        <LoadingState label="Loading your Control Center…" />
      </div>
    )
  }

  // ── Auth guards ────────────────────────────────────────────────────────────
  // Not signed in → the SMS-code login surface (same-origin, migration Phase 1
  // step 4). Rendered inline (chrome-less), so it serves at the SPA root on
  // app.justtextava.com and under /control-center on the Firebase build alike.
  if (!user && !demoMode) {
    return <ClientLogin />
  }
  // Signed in but wrong role (e.g. a superAdmin who isn't impersonating) — a login
  // form would be wrong here; offer a clean sign-out instead.
  if (unauthorized && !demoMode) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: 'var(--bg-body)' }}>
        <div className="card w-full max-w-sm p-8 text-center">
          <h1 className="text-xl font-bold theme-text mb-2">No Control Center access</h1>
          <p className="text-sm theme-text-secondary mb-6">
            This account isn’t linked to a Control Center. Sign out and sign in with the mobile number on your account.
          </p>
          <button className="btn-secondary btn-block" onClick={() => signOut(auth)}>Sign out</button>
        </div>
      </div>
    )
  }

  // Map the static CLIENT_NAV_ITEMS into the Sidebar's NavItem shape.
  // Divider entries pass through untouched; link entries get a `badge` field
  // (currently always undefined — wired for future live-count badges).
  const staticNavItems = CLIENT_NAV_ITEMS.map((item) =>
    'divider' in item
      ? item
      // Rebase the '/control-center/*' hrefs onto the deploy base so the same
      // build routes correctly on both bestaisolutions.com/control-center and
      // app.justtextava.com (root). See basePath.ts.
      : { ...item, href: ccPath(item.href.replace(/^\/control-center/, '')), badge: undefined as number | undefined }
  )

  /**
   * Setup is PERMANENT (John, 2026-09-20) — it used to vanish once setup
   * completed.
   *
   * ⚠️ WHY IT CHANGED. The tab is the Control Center REPLICA of
   * `/roadmap/{token}`, and it now carries a second view: the replica of
   * `/previews/{token}` — "everything Ava has made for you". The roadmap half
   * does go quiet when a client is all set; **what we made does not.** Hiding the
   * tab on completion would take the durable half with it, and John's concern was
   * exactly that: "that setup tab disappears and they won't be able to access it
   * anymore."
   *
   * The badge still only appears when something is actually pending, so a
   * finished client sees a calm tab rather than a nagging one.
   */
  // Prospecting appears only for a client who has the skill (2026-10-11) — the static
  // list is the same for everyone, and a tab for a skill they do not have is noise.
  // Mirrors backend prospecting/skillGate.isProspectingEnabled.
  // 2026-11-04: hired OR switched on (the comped beta clients) — the backend's one resolver.
  const hasProspecting = (client as { prospecting?: { enabled?: unknown } } | null | undefined)?.prospecting?.enabled === true
    || (((client as { hiredAgents?: unknown } | null | undefined)?.hiredAgents as unknown[] | undefined) ?? []).includes('prospecting')
  const prospectingNavItem = { id: 'prospecting', label: 'Prospecting', icon: '🎯', href: ccPath('/prospecting'), badge: undefined as number | undefined }
  const withProspecting: Array<(typeof staticNavItems)[number] | typeof prospectingNavItem> = []
  for (const item of staticNavItems) {
    withProspecting.push(item)
    if (hasProspecting && 'id' in item && item.id === 'activity') withProspecting.push(prospectingNavItem)
  }

  const navItems = [
    {
      ...CLIENT_SETUP_NAV_ITEM,
      href: ccPath('/setup'),
      badge: pendingSetupCount > 0 ? pendingSetupCount : undefined,
    },
    ...withProspecting,
  ]

  return (
    <div className="flex h-screen overflow-hidden" style={{ backgroundColor: 'var(--bg-body)' }}>
      {/* Sidebar */}
      <Sidebar
        navItems={navItems}
        appName="Control Center"
        brand={<JtaWordmark />}
        isDarkMode={isDarkMode}
        mobileOpen={mobileMenuOpen}
        onMobileClose={() => setMobileMenuOpen(false)}
        footer={
          <>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--accent)' }}>
                <span className="text-xs font-bold text-white">
                  {(client?.ownerName ?? client?.businessName ?? 'A')
                    .split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2)}
                </span>
              </div>
              <div className="min-w-0">
                {client?.ownerName && (
                  <div className="text-sm font-semibold theme-text truncate">{client.ownerName}</div>
                )}
                <div className="text-xs theme-text-muted truncate">{client?.businessName ?? ''}</div>
              </div>
            </div>
            {/* Two-tier brand (project_brand_architecture): JustTextAva is the
                consumer flagship, Best AI Solutions the corporate parent. The
                attribution stays — dropping it would be the wrong fix — but it
                reads the way the LOGIN already phrases it, instead of stamping
                the parent's name on the client's own dashboard. */}
            <div className="text-xs theme-text-muted text-center mt-2">
              JustTextAva is a product of Best AI Solutions
            </div>
          </>
        }
      />

      {/* Main content area */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        {/* Impersonation banner (Super Admin viewing as client) */}
        {isImpersonating && clientId && <ImpersonationBanner clientId={clientId} businessName={client?.businessName} reviewMode={isReviewMode} />}

        {/* Header with Location Switcher */}
        <Header
          title={client?.businessName ?? 'Control Center'}
          statusBadge={(() => {
            const raw = client?.hiredAgents
            const n = Array.isArray(raw) ? raw.length : raw ? Object.keys(raw).length : 0
            if (n === 0) return undefined
            return (
              <button
                onClick={() => navigate(ccPath('/settings'))}
                className="badge badge-blue gap-1.5 hover:opacity-90"
                title="Your hired skills and monthly total"
              >
                {n} Skill{n === 1 ? '' : 's'} — ${n * AGENT_PRICE}/mo
              </button>
            )
          })()}
          user={{
            displayName: user?.displayName ?? (demoMode ? 'Mike Thompson (Demo)' : 'My Account'),
            email: user?.email ?? (demoMode ? 'mike@treasurevalleyplumbing.com' : ''),
            photoURL: user?.photoURL ?? undefined,
            // CC review #5: the ACCOUNT is the business — its logo, then its site's
            // favicon, then the signed-in user's photo, then the business initials.
            avatarUrls: [
              client?.logoUrl,
              faviconFor(client?.website),
              user?.photoURL ?? undefined,
            ].filter((u): u is string => !!u),
            initialsFrom: client?.businessName,
          }}
          onDarkModeToggle={() => setIsDarkMode((prev) => !prev)}
          isDarkMode={isDarkMode}
          onMenuToggle={() => setMobileMenuOpen((prev) => !prev)}
          onProfileClick={() => navigate(ccPath('/settings'))}
          onNotificationsClick={() => navigate(ccPath('/settings'))}
          onSettingsClick={() => navigate(ccPath('/settings'))}
          onSignOut={() => signOut(auth)}
          titleSlot={
            <LocationSwitcher
              locations={switcherLocations}
              activeLocationId={activeLocation}
              onLocationChange={setActiveLocation}
            />
          }
        />

        {/* Page content */}
        {/* ⚠️ `pb-28` CLEARS THE FLOATING AVA WIDGET (John, 2026-09-22 CC review #34).
            The widget is `fixed bottom-6 right-6`, so page content scrolls underneath
            it. Mid-scroll that is transient — you scroll and the button reappears — but
            content at the very BOTTOM of a page can never clear it, which is where John
            found "Copy Embed Code" half-covered and unclickable at narrower widths.
            Padding the scroll container is the fix that works at every viewport height,
            rather than moving the widget (it would collide somewhere else) or shrinking
            it (the overlap would be smaller, not absent). */}
        <main className="flex-1 overflow-y-auto scrollbar-thin p-4 md:p-6 pb-28 md:pb-28">
          <Outlet />
        </main>
      </div>

      {/* Floating "Chat with Ava" widget — global, bottom-right (prototype parity).
          Wired to /api/ava-chat (control-center context); identity props ground
          Ava's answers in this client's business + hired skills. */}
      <AvaChatWidget
        greeting={`Hi! I'm Ava, ${client?.businessName ?? 'your business'}'s assistant. Ask me anything — or just text me at the number we set up.`}
        clientName={client?.ownerName}
        businessName={client?.businessName}
        hiredAgents={Object.keys(client?.hiredAgents ?? {})}
        // Identity, not decoration: sent with an ID token and verified server-side,
        // which is what lets an owner's request be attributed to them. Undefined in
        // demo mode (no signed-in user), so those turns stay anonymous.
        clientId={demoMode ? undefined : clientId ?? undefined}
      />
    </div>
  )
}
